import type { MassDataPoint } from "src/store/api/generatedApi";

export interface AreaTrace {
  readonly area: string;
  readonly timesMs: number[];
  readonly values: number[];
}

// One chronological trace per area; the chart needs no other structure from
// the mass points.
export const traceScaleWeights = (
  points: readonly MassDataPoint[]
): Map<string, AreaTrace> => {
  const traces = new Map<string, AreaTrace>();
  for (const point of points) {
    let trace = traces.get(point.area);
    if (trace === undefined) {
      trace = { area: point.area, timesMs: [], values: [] };
      traces.set(point.area, trace);
    }
    trace.timesMs.push(new Date(point.timestamp).getTime());
    trace.values.push(point.value);
  }
  return traces;
};

// Decimate by keeping each bucket's first, min, max and last point, so steps
// and dips survive at positions a plain stride would move or drop.
const decimate = (
  timesMs: readonly number[],
  values: readonly number[],
  maxPoints: number
): { timesMs: number[]; values: number[] } => {
  if (timesMs.length <= maxPoints) {
    return { timesMs: [...timesMs], values: [...values] };
  }
  const bucketCount = Math.max(Math.floor(maxPoints / 4), 1);
  const bucketSize = timesMs.length / bucketCount;
  const kept = new Set<number>();
  for (let bucket = 0; bucket < bucketCount; bucket += 1) {
    const start = Math.floor(bucket * bucketSize);
    const end = Math.min(Math.floor((bucket + 1) * bucketSize), timesMs.length);
    if (end <= start) continue;
    let minIndex = start;
    let maxIndex = start;
    for (let i = start + 1; i < end; i += 1) {
      if ((values[i] ?? 0) < (values[minIndex] ?? 0)) minIndex = i;
      if ((values[i] ?? 0) > (values[maxIndex] ?? 0)) maxIndex = i;
    }
    kept.add(start);
    kept.add(minIndex);
    kept.add(maxIndex);
    kept.add(end - 1);
  }
  const indices = [...kept].sort((a, b) => a - b);
  return {
    timesMs: indices.map((i) => timesMs[i] ?? 0),
    values: indices.map((i) => values[i] ?? 0),
  };
};

// Keep the points inside the domain plus one neighbour either side (so lines
// run through the edges), then decimate anything beyond maxPoints.
export const clipTrace = (
  trace: AreaTrace,
  domainStartMs: number,
  domainEndMs: number,
  maxPoints: number
): AreaTrace | null => {
  let first = trace.timesMs.findIndex((ms) => ms >= domainStartMs);
  if (first === -1) first = trace.timesMs.length;
  let last = first;
  while (
    last < trace.timesMs.length &&
    (trace.timesMs[last] ?? Infinity) <= domainEndMs
  )
    last += 1;
  const from = Math.max(first - 1, 0);
  const to = Math.min(last + 1, trace.timesMs.length);
  if (to - from < 2) return null;

  return {
    area: trace.area,
    ...decimate(
      trace.timesMs.slice(from, to),
      trace.values.slice(from, to),
      maxPoints
    ),
  };
};

// Round up to 1/2/2.5/5 × 10^k for a stable, readable y-axis ceiling. The 2.5
// step matters for scale weights: buckets top out just above 20 t, and the
// next stop at 50 would leave the upper half of the chart empty.
export const niceCeil = (value: number): number => {
  if (value <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (value <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
};
